import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { KeyRound, Lock, Mail } from 'lucide-react';
import { Button, Notice, TextField } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

/** Recuperación de contraseña: correo → código de 6 dígitos → nueva contraseña. */
export function ForgotPasswordPage() {
  const history = useHistory();
  const toast = useToast();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const validPassword = password.length >= 10 && /[A-Za-z]/.test(password) && /\d/.test(password);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = () =>
    run(async () => {
      await api('/auth/password/forgot', { body: { email: email.trim() } });
      setStep('code');
    });

  const reset = () =>
    run(async () => {
      await api('/auth/password/reset', { body: { email: email.trim(), code, newPassword: password } });
      toast('Contraseña actualizada. Ya puedes entrar.', 'success');
      history.replace('/login');
    });

  return (
    <AuthLayout
      title="Recuperar contraseña"
      subtitle={step === 'email' ? 'Te enviaremos un código a tu correo.' : `Si ${email} tiene una cuenta, te enviamos un código de 6 dígitos. Vence en 15 minutos.`}
      footer={
        <Link to="/login" className="font-semibold text-brand-red">
          Volver a iniciar sesión
        </Link>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void (step === 'email' ? sendCode() : reset());
        }}
      >
        {error && <Notice tone="danger">{error}</Notice>}
        {step === 'email' ? (
          <>
            <TextField label="Correo electrónico" type="email" autoComplete="email" inputMode="email" icon={<Mail className="size-4" />} value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button type="submit" size="lg" block loading={busy} disabled={!email.includes('@')}>
              Enviar código
            </Button>
          </>
        ) : (
          <>
            <TextField label="Código" inputMode="numeric" autoComplete="one-time-code" maxLength={6} icon={<KeyRound className="size-4" />} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
            <TextField label="Nueva contraseña" hint="Mínimo 10 caracteres, con letras y números" type="password" autoComplete="new-password" icon={<Lock className="size-4" />} value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" size="lg" block loading={busy} disabled={code.length !== 6 || !validPassword}>
              Cambiar contraseña
            </Button>
            <button type="button" className="tap text-sm text-[var(--text-muted)] underline" onClick={() => void sendCode()}>
              Enviar otro código
            </button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
